<template>
  <div class="cost-configuration">
    <div class="section-header">
      <h4 class="gm-section-title">Skill Costs</h4>
      <button type="button" @click="addCost" class="gm-btn gm-btn--sm">
        Add Cost
      </button>
    </div>

    <div v-if="costs.length === 0" class="gm-empty">
      <p class="gm-empty__hint">No costs defined. Click "Add Cost" to add skill costs.</p>
    </div>

    <div v-for="(cost, index) in costs" :key="index" class="gm-panel cost-item">
      <div class="cost-fields">
        <div class="gm-field">
          <label class="gm-label">Cost Type</label>
          <select v-model="cost.kind" class="gm-select">
            <option :value="AttributesEnum.Health">Health</option>
            <option :value="AttributesEnum.Energy">Energy</option>
            <option :value="AttributesEnum.ActionPoints">Action Points</option>
          </select>
        </div>

        <div class="gm-field">
          <label class="gm-label">Value</label>
          <input
            v-model.number="cost.value"
            type="number"
            min="0"
            class="gm-input"
            placeholder="0"
          />
        </div>

        <button type="button" @click="removeCost(index)" class="gm-btn gm-btn--sm gm-btn--danger">
          Remove
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { AttributesEnum, type CostRequest } from '@/api/dx-backend'

// Props
interface Props {
  modelValue: CostRequest[]
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: CostRequest[]]
}>()

// Reactive state
const costs = ref<CostRequest[]>([...props.modelValue])

// Methods
const addCost = () => {
  costs.value.push({
    kind: AttributesEnum.Health,
    value: 0
  })
  emitUpdate()
}

const removeCost = (index: number) => {
  costs.value.splice(index, 1)
  emitUpdate()
}

const emitUpdate = () => {
  emit('update:modelValue', [...costs.value])
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  costs.value = [...newValue]
}, { deep: true })
</script>

<style scoped>
.cost-configuration {
  margin-bottom: var(--gm-gap-lg);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
}

.cost-item {
  margin-bottom: var(--gm-gap-sm);
}

.cost-fields {
  display: flex;
  gap: var(--gm-gap);
  align-items: end;
}

.gm-field {
  flex: 1;
}
</style>
